import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as d3 from 'd3';
import type { SocketCloudNetworkTransportTypes } from '@aaimplatform/socket-cloud-types';
// Local types for network analysis


export interface NetworkNode {
  id: string;
  label: string;
  type: 'leader' | 'follower' | 'candidate' | 'observer' | 'gateway';
  status: 'healthy' | 'degraded' | 'unhealthy' | 'offline';
  metrics: {
    cpu: number;
    memory: number;
    network: number;
    consensus: number;
    connections: number;
    messageRate: number;
    errorRate: number;
  };
  connections: string[];
  lastSeen: Date;
  region?: string;
  datacenter?: string;
  tags: string[];
}

export type MessageFlow = SocketCloudNetworkTransportTypes.components['schemas']['SocketCloudMessageFlow'];

export type NetworkPartition = SocketCloudNetworkTransportTypes.components['schemas']['NetworkPartition'];

export type PerformanceBottleneck = SocketCloudNetworkTransportTypes.components['schemas']['NetworkBottleneck'];

type NetworkAnalysisResult = SocketCloudNetworkTransportTypes.components['schemas']['NetworkAnalysisResult'];

interface InteractiveNetworkAnalysisProps {
  _nodes: NetworkNode[];
  messageFlows: MessageFlow[];
  partitions: NetworkPartition[];
  bottlenecks: PerformanceBottleneck[];
  selectedNodes: string[];
  onNodeSelection: (nodeIds: string[]) => void;
  onAnalysisRun: (analysisType: string, params: any) => Promise<NetworkAnalysisResult>;
  realTimeMode?: boolean;
}

/**
 * Node selection and inspection panel
 */
function NodeInspectionPanel({
  _nodes,
  selectedNodes,
  onNodeSelection,
  onAnalysisRun,
}: {
  _nodes: NetworkNode[];
  selectedNodes: string[];
  onNodeSelection: (nodeIds: string[]) => void;
  onAnalysisRun: (analysisType: string, params: any) => Promise<NetworkAnalysisResult>;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('connections');
  const [analysisResults, setAnalysisResults] = useState<NetworkAnalysisResult | null>(null);

  const filteredNodes = useMemo(() => {
    let filtered = _nodes;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((node: NetworkNode) => 
        node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter((node: NetworkNode) => node.type === filterType);
    }

    // Apply sorting
    filtered.sort((a: NetworkNode, b: NetworkNode) => {
      switch (sortBy) {
        case 'connections':
          return b.connections.length - a.connections.length;
        case 'cpu':
          return b.metrics.cpu - a.metrics.cpu;
        case 'memory':
          return b.metrics.memory - a.metrics.memory;
        case 'network':
          return b.metrics.network - a.metrics.network;
        case 'messageRate':
          return b.metrics.messageRate - a.metrics.messageRate;
        case 'errorRate':
          return b.metrics.errorRate - a.metrics.errorRate;
        case 'lastSeen':
          return b.lastSeen.getTime() - a.lastSeen.getTime();
        default:
          return a.label.localeCompare(b.label);
      }
    });

    return filtered;
  }, [_nodes, searchTerm, filterType, sortBy]);

  const selectedNodeDetails = useMemo(() => {
    return _nodes.filter((node: NetworkNode) => selectedNodes.includes(node.id));
  }, [_nodes, selectedNodes]);

  const handleNodeToggle = useCallback((nodeId: string) => {
    const newSelection = selectedNodes.includes(nodeId)
      ? selectedNodes.filter(id => id !== nodeId)
      : [...selectedNodes, nodeId];
    onNodeSelection(newSelection);
  }, [selectedNodes, onNodeSelection]);

  const handleSelectAll = useCallback(() => {
    onNodeSelection(filteredNodes.map((node: NetworkNode) => node.id));
  }, [filteredNodes, onNodeSelection]);

  const handleClearSelection = useCallback(() => {
    onNodeSelection([]);
  }, [onNodeSelection]);

  const runAnalysis = useCallback(async (analysisType: string) => {
    const params = {
      nodeIds: selectedNodes,
      includeMessageFlow: true,
      timeWindow: 300000, // 5 minutes
    };

    try {
      const results = await onAnalysisRun(analysisType, params);
      setAnalysisResults(results);
    } catch (error) {
      console.error('Analysis failed:', error);
    }
  }, [selectedNodes, onAnalysisRun]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return '#10b981';
      case 'degraded': return '#f59e0b';
      case 'unhealthy': return '#ef4444';
      case 'offline': return '#6b7280';
      default: return '#374151';
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and filters */}
      <div className="bg-gray-900 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-white mb-4">Node Selection</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-gray-800 rounded px-3 py-2 text-white"
          />
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-gray-800 rounded px-3 py-2 text-white"
          >
            <option value="all">All Types</option>
            <option value="leader">Leaders</option>
            <option value="follower">Followers</option>
            <option value="candidate">Candidates</option>
            <option value="observer">Observers</option>
            <option value="gateway">Gateways</option>
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-gray-800 rounded px-3 py-2 text-white"
          >
            <option value="connections">Connections</option>
            <option value="cpu">CPU Usage</option>
            <option value="memory">Memory Usage</option>
            <option value="network">Network Usage</option>
            <option value="messageRate">Message Rate</option>
            <option value="errorRate">Error Rate</option>
            <option value="lastSeen">Last Seen</option>
          </select>
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={handleSelectAll}
            className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
          >
            Select All ({filteredNodes.length})
          </button>
          <button
            onClick={handleClearSelection}
            className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
          >
            Clear Selection
          </button>
          <span className="text-gray-400 text-sm py-1">
            {selectedNodes.length} selected
          </span>
        </div>

        {/* Node list */}
        <div className="max-h-96 overflow-y-auto space-y-2">
          {filteredNodes.map((node: NetworkNode) => (
            <motion.div
              key={node.id}
              className={`bg-gray-800 rounded-lg p-3 cursor-pointer hover:bg-gray-700 transition-colors ${
                selectedNodes.includes(node.id) ? 'bg-blue-500/10 ring-2 ring-blue-400' : ''
              }`}
              onClick={() => handleNodeToggle(node.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedNodes.includes(node.id)}
                    onChange={() => handleNodeToggle(node.id)}
                    className="rounded"
                  />
                  <span className="font-medium text-white">{node.label}</span>
                  <span className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                    {node.type}
                  </span>
                </div>
                <span className="text-gray-400 text-sm">
                  {node.connections.length} connections
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2 text-xs">
                <div>
                  <span className="text-gray-400">CPU:</span>
                  <span className={`ml-1 ${node.metrics.cpu > 80 ? 'text-danger-400' : 'text-primary-400'}`}>
                    {node.metrics.cpu.toFixed(1)}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Memory:</span>
                  <span className={`ml-1 ${node.metrics.memory > 80 ? 'text-danger-400' : 'text-status-online'}`}>
                    {node.metrics.memory.toFixed(1)}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Network:</span>
                  <span className="text-yellow-400 ml-1">{node.metrics.network.toFixed(1)}%</span>
                </div>
                <div>
                  <span className="text-gray-400">Errors:</span>
                  <span className={`ml-1 ${node.metrics.errorRate > 0.05 ? 'text-danger-400' : 'text-gray-400'}`}>
                    {(node.metrics.errorRate * 100).toFixed(2)}%
                  </span>
                </div>
              </div>

              {node.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {node.tags.map((tag: string) => (
                    <span key={tag} className="text-xs bg-secondary-600/20 text-secondary-300 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Analysis tools */}
      {selectedNodes.length > 0 && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Analysis Tools</h3>
          
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => runAnalysis('path_analysis')}
              className="px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Path Analysis
            </button>
            <button
              onClick={() => runAnalysis('bottleneck_detection')}
              className="px-3 py-2 bg-secondary-600 text-white rounded hover:bg-secondary-700"
            >
              Bottleneck Detection
            </button>
            <button
              onClick={() => runAnalysis('message_flow')}
              className="px-3 py-2 bg-status-online text-white rounded hover:bg-status-online-dark"
            >
              Message Flow Analysis
            </button>
            <button
              onClick={() => runAnalysis('partition_detection')}
              className="px-3 py-2 bg-danger-600 text-white rounded hover:bg-danger-700"
            >
              Partition Detection
            </button>
          </div>
        </div>
      )}

      {/* Selected node details */}
      {selectedNodeDetails.length > 0 && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">
            Selected Nodes ({selectedNodeDetails.length})
          </h3>
          
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {selectedNodeDetails.map((node: NetworkNode) => (
              <div key={node.id} className="bg-gray-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">{node.label}</span>
                  <button
                    onClick={() => handleNodeToggle(node.id)}
                    className="text-danger-400 hover:text-danger-300"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div>ID: <span className="text-gray-400 font-mono">{node.id.slice(-12)}</span></div>
                    <div>Type: <span className="text-yellow-400">{node.type}</span></div>
                    <div>Status: <span style={{ color: getStatusColor(node.status) }}>{node.status}</span></div>
                  </div>
                  <div>
                    <div>Region: <span className="text-primary-400">{node.region || 'Unknown'}</span></div>
                    <div>DC: <span className="text-secondary-400">{node.datacenter || 'Unknown'}</span></div>
                    <div>Messages: <span className="text-status-online">{node.metrics.messageRate.toFixed(0)}/s</span></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis results */}
      {analysisResults && (
        <div className="bg-gray-900 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Analysis Results</h3>
          
          <div className="space-y-4">
            {/* Network health summary */}
            <div className="bg-gray-800 rounded-lg p-3">
              <h4 className="font-medium text-white mb-2">Network Health</h4>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-400">
                    {(analysisResults.networkHealth.overall * 100).toFixed(0)}%
                  </div>
                  <div className="text-gray-400">Overall</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-status-online">
                    {(analysisResults.networkHealth.connectivity * 100).toFixed(0)}%
                  </div>
                  <div className="text-gray-400">Connectivity</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">
                    {(analysisResults.networkHealth.performance * 100).toFixed(0)}%
                  </div>
                  <div className="text-gray-400">Performance</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-400">
                    {(analysisResults.networkHealth.reliability * 100).toFixed(0)}%
                  </div>
                  <div className="text-gray-400">Reliability</div>
                </div>
              </div>
            </div>

            {/* Critical path */}
            {analysisResults.criticalPath.length > 0 && (
              <div className="bg-gray-800 rounded-lg p-3">
                <h4 className="font-medium text-white mb-2">Critical Path</h4>
                <div className="text-sm text-gray-300">
                  {analysisResults.criticalPath.map(nodeId => 
                    _nodes.find((n: NetworkNode) => n.id === nodeId)?.label || nodeId.slice(-8),
                  ).join(' → ')}
                </div>
              </div>
            )}

            {/* Bottlenecks */}
            {analysisResults.bottlenecks.length > 0 && (
              <div className="bg-gray-800 rounded-lg p-3">
                <h4 className="font-medium text-white mb-2">Performance Bottlenecks</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {analysisResults.bottlenecks.map(bottleneck => (
                    <div key={bottleneck.id} className="text-sm">
                      <span className="text-danger-400">{bottleneck.type}</span> on{' '}
                      <span className="text-primary-400">
                        {_nodes.find((n: NetworkNode) => n.id === bottleneck.nodeId)?.label || bottleneck.nodeId.slice(-8)}
                      </span>
                      : {bottleneck.value.toFixed(1)} (threshold: {bottleneck.threshold})
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Message flow analysis visualization
 */
function MessageFlowAnalysis({
  messageFlows,
  selectedNodes,
}: {
  messageFlows: MessageFlow[];
  selectedNodes: string[];
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [_hoveredFlow, _setHoveredFlow] = useState<MessageFlow | null>(null);

  const filteredFlows = useMemo(() => {
    if (selectedNodes.length === 0) {
return messageFlows;
}
    
    return messageFlows.filter(flow => 
      selectedNodes.includes(flow.source) || selectedNodes.includes(flow.target),
    );
  }, [messageFlows, selectedNodes]);

  const flowStats = useMemo(() => {
    const stats = {
      totalMessages: filteredFlows.length,
      successfulMessages: filteredFlows.filter(f => f.success).length,
      averageLatency: filteredFlows.reduce((sum, f) => sum + (f.latency ?? 0), 0) / filteredFlows.length || 0,
      totalPayload: filteredFlows.reduce((sum, f) => sum + (f.payload ?? 0), 0),
      messageTypes: new Map<string, number>(),
      routeLengths: new Map<number, number>(),
    };

    filteredFlows.forEach(flow => {
      // Count message types
      const currentCount = stats.messageTypes.get(flow.messageType ?? 'unknown') || 0;
      stats.messageTypes.set(flow.messageType ?? 'unknown', currentCount + 1);

      // Count route lengths
      const routeLength = (flow.route ?? []).length;
      const currentRouteCount = stats.routeLengths.get(routeLength) || 0;
      stats.routeLengths.set(routeLength, currentRouteCount + 1);
    });

    return stats;
  }, [filteredFlows]);

  useEffect(() => {
    if (!svgRef.current || filteredFlows.length === 0) {
return;
}

    const svg = d3.select(svgRef.current);
    const margin = { top: 20, right: 40, bottom: 40, left: 60 };
    const width = 600 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    svg.selectAll('*').remove();

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Group flows by time buckets (5-second intervals)
    const timeBuckets = new Map<number, MessageFlow[]>();
    const bucketSize = 5000; // 5 seconds

    filteredFlows.forEach(flow => {
      const bucket = Math.floor(flow.timestamp / bucketSize) * bucketSize;
      if (!timeBuckets.has(bucket)) {
        timeBuckets.set(bucket, []);
      }
      timeBuckets.get(bucket)!.push(flow);
    });

    const bucketData = Array.from(timeBuckets.entries())
      .map(([time, flows]) => ({
        time: new Date(time),
        count: flows.length,
        successCount: flows.filter(f => f.success).length,
        averageLatency: flows.reduce((sum, f) => sum + (f.latency ?? 0), 0) / flows.length,
        totalPayload: flows.reduce((sum, f) => sum + (f.payload ?? 0), 0),
      }))
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    if (bucketData.length === 0) {
return;
}

    // Time scale
    const timeScale = d3.scaleTime()
      .domain(d3.extent(bucketData, d => d.time) as [Date, Date])
      .range([0, width]);

    // Count scale
    const countScale = d3.scaleLinear()
      .domain([0, d3.max(bucketData, d => d.count) || 0])
      .range([height, 0]);

    // Draw axes
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(timeScale).tickFormat((d) => d3.timeFormat('%H:%M:%S')(d as Date)))
      .selectAll('text')
      .attr('fill', '#9ca3af');

    g.append('g')
      .call(d3.axisLeft(countScale))
      .selectAll('text')
      .attr('fill', '#9ca3af');

    // Draw message count line
    const line = d3.line<typeof bucketData[0]>()
      .x(d => timeScale(d.time))
      .y(d => countScale(d.count))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(bucketData)
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 2)
      .attr('d', line);

    // Draw success rate bars
    g.selectAll('.success-bar')
      .data(bucketData)
      .enter()
      .append('rect')
      .attr('class', 'success-bar')
      .attr('x', d => timeScale(d.time) - 2)
      .attr('y', d => countScale(d.successCount))
      .attr('width', 4)
      .attr('height', d => height - countScale(d.successCount))
      .attr('fill', '#10b981')
      .attr('opacity', 0.6);

  }, [filteredFlows]);

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Message Flow Analysis</h3>
      
      {/* Flow statistics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-primary-400">{flowStats.totalMessages}</div>
          <div className="text-sm text-gray-400">Total Messages</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-status-online">
            {((flowStats.successfulMessages / flowStats.totalMessages) * 100).toFixed(1)}%
          </div>
          <div className="text-sm text-gray-400">Success Rate</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-yellow-400">
            {flowStats.averageLatency.toFixed(0)}ms
          </div>
          <div className="text-sm text-gray-400">Avg Latency</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-secondary-400">
            {(flowStats.totalPayload / 1024 / 1024).toFixed(1)}MB
          </div>
          <div className="text-sm text-gray-400">Total Payload</div>
        </div>
      </div>

      {/* Flow timeline */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-white mb-2">Message Timeline</h4>
        <svg
          ref={svgRef}
          width="600"
          height="300"
          className="rounded bg-gray-800"
        />
      </div>

      {/* Message type breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-md font-medium text-white mb-2">Message Types</h4>
          <div className="space-y-2">
            {Array.from(flowStats.messageTypes.entries())
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between bg-gray-800 rounded p-2">
                  <span className="text-gray-300">{type}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-primary-500 h-2 rounded-full"
                        style={{ width: `${(count / flowStats.totalMessages) * 100}%` }}
                      />
                    </div>
                    <span className="text-primary-400 font-mono text-sm w-12 text-right">{count}</span>
                  </div>
                </div>
              ))
            }
          </div>
        </div>

        <div>
          <h4 className="text-md font-medium text-white mb-2">Route Lengths</h4>
          <div className="space-y-2">
            {Array.from(flowStats.routeLengths.entries())
              .sort((a, b) => a[0] - b[0])
              .map(([length, count]) => (
                <div key={length} className="flex items-center justify-between bg-gray-800 rounded p-2">
                  <span className="text-gray-300">{length} hops</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-gray-700 rounded-full h-2">
                      <div 
                        className="bg-status-online h-2 rounded-full"
                        style={{ width: `${(count / flowStats.totalMessages) * 100}%` }}
                      />
                    </div>
                    <span className="text-status-online font-mono text-sm w-12 text-right">{count}</span>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Network export and reporting tools
 */
function ExportTools({
  selectedNodes,
  analysisResults,
}: {
  selectedNodes: string[];
  analysisResults: NetworkAnalysisResult | null;
}) {
  const [exportFormat, setExportFormat] = useState<'json' | 'csv' | 'pdf' | 'svg'>('json');
  const [includeMetrics, setIncludeMetrics] = useState(true);
  const [includeTopology, setIncludeTopology] = useState(true);

  const handleExport = useCallback(() => {
    const exportData = {
      timestamp: new Date().toISOString(),
      selectedNodes,
      analysisResults,
      includeMetrics,
      includeTopology,
      format: exportFormat,
    };

    // In a real implementation, this would generate and download the file
    // console.log('Exporting _data:', exportData);
    
    // Create download link
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = '_data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `network-analysis-${Date.now()}.${exportFormat}`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }, [selectedNodes, analysisResults, exportFormat, includeMetrics, includeTopology]);

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-white mb-4">Export & Reporting</h3>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="export-format" className="block text-sm font-medium text-gray-300 mb-2">Export Format</label>
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as any)}
            className="w-full bg-gray-800 rounded px-3 py-2 text-white"
          >
            <option value="json">JSON</option>
            <option value="csv">CSV</option>
            <option value="pdf">PDF Report</option>
            <option value="svg">SVG Diagram</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeMetrics}
              onChange={(e) => setIncludeMetrics(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-300">Include node metrics</span>
          </label>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={includeTopology}
              onChange={(e) => setIncludeTopology(e.target.checked)}
              className="rounded"
            />
            <span className="text-gray-300">Include topology data</span>
          </label>
        </div>

        <button
          onClick={handleExport}
          disabled={selectedNodes.length === 0}
          className="w-full px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
        >
          Export Analysis ({selectedNodes.length} _nodes)
        </button>

        <div className="text-xs text-gray-400">
          Export includes: topology structure, node metrics, analysis results, and timestamp
        </div>
      </div>
    </div>
  );
}

/**
 * Main interactive network analysis component
 */
export function InteractiveNetworkAnalysis({
  _nodes,
  messageFlows,
  partitions: _partitions,
  bottlenecks: _bottlenecks,
  selectedNodes,
  onNodeSelection,
  onAnalysisRun,
  realTimeMode = false,
}: InteractiveNetworkAnalysisProps) {
  const [analysisResults, setAnalysisResults] = useState<NetworkAnalysisResult | null>(null);

  const handleAnalysisRun = useCallback(async (analysisType: string, params: any) => {
    const results = await onAnalysisRun(analysisType, params);
    setAnalysisResults(results);
    return results;
  }, [onAnalysisRun]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Interactive Network Analysis</h2>
        <div className="flex items-center gap-4">
          {realTimeMode && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-status-online rounded-full animate-pulse" />
              <span className="text-status-online text-sm">Real-time</span>
            </div>
          )}
          <span className="text-gray-400 text-sm">
            {_nodes.length} nodes, {messageFlows.length} flows
          </span>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left column - Node selection and analysis tools */}
        <div className="xl:col-span-1">
          <NodeInspectionPanel
            _nodes={_nodes}
            selectedNodes={selectedNodes}
            onNodeSelection={onNodeSelection}
            onAnalysisRun={handleAnalysisRun}
          />
        </div>

        {/* Right columns - Analysis results */}
        <div className="xl:col-span-2 space-y-6">
          <MessageFlowAnalysis
            messageFlows={messageFlows}
            selectedNodes={selectedNodes}
          />
          
          <ExportTools
            selectedNodes={selectedNodes}
            analysisResults={analysisResults}
          />
        </div>
      </div>
    </div>
  );
}

export default InteractiveNetworkAnalysis;